"use client";

import { FetchButton } from "@/fetch-ui/FetchButton";
import { deriveVisibleTypes, sumNightCounts } from "@/features/event-details-rooms/_logic/rooms";
import { ROOM_TYPES, type RoomType } from "@/lib/utils/accommodations";
import { AnimatePresence, motion } from "motion/react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { formElementVariants, isBelowRoomMinimum } from "../../_logic/constants";
import { asRange } from "../../_logic/dates";
import { clearRoomTypeCounts } from "../../_logic/guestroom-nights";
import {
  accommodationsPeakRoomCount,
  hasAccommodationsCount,
  hasAccommodationsDates,
} from "../../_logic/step-completion";
import { useShoulderNights } from "../../_hooks/use-shoulder-nights";
import { useSubPhase } from "../../_hooks/use-sub-phase";
import { GuestroomSubPhase, RoomTypeColumns, RoomTypeCounts, StepProps } from "../../_types/wizard-types";
import BelowMinRoomsModal from "../BelowMinRoomsModal";
import { GuestroomsGrid } from "./guestrooms/GuestroomsGrid";
import { SubPhasePanel } from "./SubPhasePanel";
import { YesNoGate } from "./YesNoGate";

const GUESTROOM_PHASES: readonly GuestroomSubPhase[] = ["gate", "count"];

/**
 * Step: Guestroom requirements — Yes/No gate, then a per-night/per-room-type breakdown grid. Program
 * dates are derived from the Dates step's preferred event date rather than asked again here; the
 * planner can add pre/post nights around them in the grid without changing the program dates.
 *
 * @param props - Wizard form data, dispatch, and the step's next/back navigation.
 * @returns The guestrooms step.
 */
export const GuestroomsStep = ({ formData, dispatch, onNext, onBack }: StepProps) => {
  // Hooks
  const {
    phase: subPhase,
    direction: subDirection,
    goTo,
    goBack: handleSubBack,
  } = useSubPhase(GUESTROOM_PHASES, formData.accommodations.needed ? "count" : "gate", onBack);
  const { stayNights, shoulders } = useShoulderNights({
    checkIn: formData.accommodations.checkIn,
    checkOut: formData.accommodations.checkOut,
    detailed: formData.accommodationsDetailed,
    dispatch,
  });

  // States
  const [minRoomsModalReason, setMinRoomsModalReason] = useState<"count" | "northstar" | null>(null);
  const [visibleTypes, setVisibleTypes] = useState<Set<RoomType>>(() =>
    deriveVisibleTypes(formData.accommodationsDetailed ?? {})
  );

  // Derived values
  const { accommodations, accommodationsDetailed } = formData;
  const hasDates = hasAccommodationsDates(formData);
  const hasCount = hasAccommodationsCount(formData);
  const onlyShoulderRooms =
    !hasCount &&
    stayNights.some((night) => night.segment !== "peak" && sumNightCounts(accommodationsDetailed?.[night.date]) > 0);
  // Northstar requests are sourced through our platform, so they must include guestrooms.
  const isNorthstar = !!formData.northstarRequestId;
  const visibleColumns = useMemo<RoomType[]>(() => Array.from(visibleTypes), [visibleTypes]);
  const availableTypesForAdd = useMemo<RoomType[]>(
    () => ROOM_TYPES.filter((t) => !visibleTypes.has(t)),
    [visibleTypes]
  );

  // Handlers
  const handleDetailChange = useCallback(
    (next: { [date: string]: RoomTypeCounts }) => {
      dispatch({ type: "SET_ACCOMMODATIONS_DETAILED", payload: next });
    },
    [dispatch]
  );

  const handleNo = useCallback(() => {
    if (isNorthstar) {
      setMinRoomsModalReason("northstar");
      return;
    }
    dispatch({ type: "SET_ACCOMMODATIONS_NEEDED", payload: false });
    onNext();
  }, [isNorthstar, dispatch, onNext]);

  const handleYes = useCallback(() => {
    dispatch({ type: "SET_ACCOMMODATIONS_NEEDED", payload: true });
    const preferredDates = asRange(formData.eventDates);
    if (preferredDates) {
      dispatch({
        type: "SET_ACCOMMODATION_DATES",
        payload: { checkIn: preferredDates.startDate, checkOut: preferredDates.endDate },
      });
    }
    goTo("count");
  }, [dispatch, goTo, formData.eventDates]);

  const handleAddType = useCallback((type: RoomType) => {
    setVisibleTypes((prev) => {
      if (prev.has(type)) return prev;
      const next = new Set(prev);
      next.add(type);
      return next;
    });
  }, []);

  const handleRemoveType = useCallback(
    (type: RoomType) => {
      if (visibleTypes.size <= 1) return;
      setVisibleTypes((prev) => {
        if (!prev.has(type)) return prev;
        const next = new Set(prev);
        next.delete(type);
        return next;
      });
      // Zero the type's counts across all nights so removal persists on save and the column
      // stays hidden if the step remounts (derived visibility = no non-zero data left). Routed
      // through the same `handleDetailChange` path as a normal cell edit, not a second dispatch.
      handleDetailChange(clearRoomTypeCounts(accommodationsDetailed ?? {}, stayNights, type));
    },
    [handleDetailChange, accommodationsDetailed, stayNights, visibleTypes.size]
  );

  // Grouped for the grid; built here because it bundles the add/remove handlers above.
  const columns = useMemo<RoomTypeColumns>(
    () => ({
      visible: visibleColumns,
      availableForAdd: availableTypesForAdd,
      onAdd: handleAddType,
      onRemove: handleRemoveType,
    }),
    [visibleColumns, availableTypesForAdd, handleAddType, handleRemoveType]
  );

  const handleCountContinue = useCallback(() => {
    const belowMinimum = isBelowRoomMinimum(accommodationsPeakRoomCount(formData));
    if (belowMinimum) {
      setMinRoomsModalReason("count");
      return;
    }
    onNext();
  }, [formData, onNext]);

  const handleContinueWithoutGuestrooms = useCallback(() => {
    setMinRoomsModalReason(null);
    dispatch({ type: "SET_ACCOMMODATIONS_NEEDED", payload: false });
    onNext();
  }, [dispatch, onNext]);

  // Effects
  // `accommodations.checkIn/checkOut` is only ever set FROM `formData.eventDates` (there's no
  // independent picker for it) — so if the planner goes back and edits the Dates step after
  // already saying "yes" to guestrooms here, this keeps the grid's nights in sync instead of
  // silently showing the stale range from whenever "Yes" was first clicked. This range stays the
  // program's own (peak) nights: pre/post nights live only as extra keys in the per-night
  // breakdown, so adding one never widens the program dates.
  useEffect(() => {
    if (!accommodations.needed) return;
    const preferredDates = asRange(formData.eventDates);
    if (!preferredDates) return;
    if (accommodations.checkIn === preferredDates.startDate && accommodations.checkOut === preferredDates.endDate) {
      return;
    }
    dispatch({
      type: "SET_ACCOMMODATION_DATES",
      payload: { checkIn: preferredDates.startDate, checkOut: preferredDates.endDate },
    });
  }, [accommodations.needed, accommodations.checkIn, accommodations.checkOut, formData.eventDates, dispatch]);

  // Render
  return (
    <div className="flex flex-col min-h-[420px]">
      <motion.div custom={0} variants={formElementVariants} initial="hidden" animate="visible">
        <h1 className="text-3xl md:text-4xl font-semibold text-gray-900 tracking-tight">Do you need guestrooms?</h1>
      </motion.div>

      <div className="flex-1 flex flex-col mt-10">
        <AnimatePresence mode="wait" custom={subDirection}>
          {subPhase === "gate" && (
            <SubPhasePanel phaseKey="gate" direction={subDirection}>
              <YesNoGate onYes={handleYes} onNo={handleNo} />
            </SubPhasePanel>
          )}

          {subPhase === "count" && (
            <SubPhasePanel phaseKey="count" direction={subDirection} className="flex flex-col gap-6">
              <p className="text-xl md:text-2xl font-semibold text-gray-900 tracking-tight">
                How many rooms do you need each night?
              </p>

              {hasDates && stayNights.length === 0 ? (
                <p className="text-sm text-gray-500">
                  Your program has no overnight stay. Extend the dates to request rooms.
                </p>
              ) : hasDates ? (
                <GuestroomsGrid
                  stayNights={stayNights}
                  detailed={accommodationsDetailed ?? {}}
                  columns={columns}
                  shoulders={shoulders}
                  numPeople={formData.numPeople}
                  onChange={handleDetailChange}
                />
              ) : (
                <p className="text-xs font-medium text-gray-400">Pick your check-in/out dates first to see the grid.</p>
              )}
              {onlyShoulderRooms && (
                <p className="text-xs font-medium text-gray-500">
                  Pre/post nights don&apos;t count toward the room minimum. Add rooms to at least one program night.
                </p>
              )}
            </SubPhasePanel>
          )}
        </AnimatePresence>
      </div>

      <div className="flex gap-3 mt-8">
        {subPhase === "gate" ? (
          onBack && (
            <FetchButton variant="outline" color="neutral" size="lg" onClick={onBack} className="flex-1">
              Back
            </FetchButton>
          )
        ) : (
          <>
            <FetchButton variant="outline" color="neutral" size="lg" onClick={handleSubBack} className="flex-1">
              Back
            </FetchButton>
            <FetchButton
              variant="primary"
              size="lg"
              onClick={handleCountContinue}
              disabled={!hasDates || !hasCount}
              className="flex-1"
            >
              Continue
            </FetchButton>
          </>
        )}
      </div>

      <BelowMinRoomsModal
        isOpen={minRoomsModalReason !== null}
        onClose={() => setMinRoomsModalReason(null)}
        reason={minRoomsModalReason ?? "count"}
        onContinueWithoutGuestrooms={minRoomsModalReason === "count" ? handleContinueWithoutGuestrooms : undefined}
      />
    </div>
  );
};
